import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, combineLatest, Subject, of } from 'rxjs';
import { map, take, filter, tap } from 'rxjs/operators';
import { TrainerService } from './trainer.service';
import { GymService } from './gym.service';

export type AccountMode = 'trainer' | 'gym';

@Injectable({
    providedIn: 'root',
})
export class AccountModeService {
    private _currentMode = new BehaviorSubject<AccountMode>('trainer');
    private _initialized = new BehaviorSubject<boolean>(false);
    
    // Event emitter for mode changes - components can subscribe to refresh navigation
    private _modeChanged = new Subject<AccountMode>();
    
    constructor(
        private _trainerService: TrainerService,
        private _gymService: GymService,
        private _router: Router
    ) {}

    get currentMode$(): Observable<AccountMode> {
        return this._currentMode.asObservable();
    }

    get currentMode(): AccountMode {
        return this._currentMode.value;
    }

    get modeChanged$(): Observable<AccountMode> {
        return this._modeChanged.asObservable();
    }

    get initialized$(): Observable<boolean> {
        return this._initialized.asObservable();
    }

    /**
     * Initialize the account mode based on available accounts.
     * Returns an observable that completes when mode is determined.
     * Call this before fetching navigation.
     */
    initialize(): Observable<AccountMode> {
        // If already initialized, return current mode immediately
        if (this._initialized.value) {
            return of(this._currentMode.value);
        }

        return combineLatest([
            this._trainerService.trainer$,
            this._gymService.gym$
        ]).pipe(
            // Wait for at least one value from each
            take(1),
            tap(([trainer, gym]) => {
                console.log('AccountModeService.initialize()', { trainer: !!trainer, gym: !!gym });
                
                // Determine initial mode based on available accounts
                if (gym && !trainer) {
                    // Only gym account - set to gym mode
                    this._currentMode.next('gym');
                } else if (trainer && !gym) {
                    // Only trainer account - set to trainer mode
                    this._currentMode.next('trainer');
                } else if (gym && trainer) {
                    // Both accounts - default to trainer mode (user can switch)
                    this._currentMode.next('trainer');
                }
                // If neither, keep default trainer mode
                
                this._initialized.next(true);
            }),
            map(() => this._currentMode.value)
        );
    }

    /**
     * Check if user has trainer account
     */
    get hasTrainerAccount$(): Observable<boolean> {
        return this._trainerService.trainer$.pipe(map(t => !!t));
    }

    /**
     * Check if user has gym account
     */
    get hasGymAccount$(): Observable<boolean> {
        return this._gymService.gym$.pipe(map(g => !!g));
    }

    /**
     * Check if user has both account types
     */
    get hasBothAccounts$(): Observable<boolean> {
        return combineLatest([
            this.hasTrainerAccount$,
            this.hasGymAccount$
        ]).pipe(map(([t, g]) => t && g));
    }

    /**
     * Switch to a different mode
     */
    switchMode(mode: AccountMode): void {
        this._currentMode.next(mode);
        this._modeChanged.next(mode);
        
        // Navigate to appropriate home page
        if (mode === 'trainer') {
            this._router.navigate(['/app/profile']);
        } else {
            this._router.navigate(['/app/gym']);
        }
    }

    /**
     * Toggle between modes (if user has both)
     */
    toggleMode(): void {
        const newMode = this._currentMode.value === 'trainer' ? 'gym' : 'trainer';
        this.switchMode(newMode);
    }

    /**
     * Reset initialization state (call on logout)
     */
    reset(): void {
        this._currentMode.next('trainer');
        this._initialized.next(false);
    }
}
